import React, { useEffect, useMemo, useState } from "react";
import { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { getNearbyZipCodes } from "../../../../util/Api/api";
import VirtualizedAsyncSelect from "./VirtualizedAsyncSelect";
import ManualInput from "./doctors.manualInput";
import { IFieldDropdown } from "./SelectDropdown.types";
import Loader from "../../../../components/Loader";

const validateZipCode = (input: string) => /^\d{5}$/.test(input);

const DoctorsDropdown = (props: { fieldData: IFieldDropdown; register: UseFormRegister<any>, errors: FieldErrors; control: Control }) => {
  const {
    fieldData,
    fieldData: {
      speciality,
      doctorsLoadingText,
      enableZipcodeSearch,
      zipcodePlaceholder,
      zipcodeResultsDistance,
      manualDoctorInput,
    },
    register,
    errors,
    control,
  } = props;

  const [zipSearchVisible, setZipSearchVisible] = useState(enableZipcodeSearch ?? false);
  const [zipCodeInput, setZipCodeInput] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [resolvedZipCodes, setResolvedZipCodes] = useState<string[] | null>(null);
  const [isResolvingZips, setIsResolvingZips] = useState(false);
  const [isManualInput, setIsManualInput] = useState(false);

  const handleChildStateChange = (newState: boolean) => setIsManualInput(newState);

  useEffect(() => {
    setZipSearchVisible(enableZipcodeSearch ? true : false);
  }, [enableZipcodeSearch]);

  useEffect(() => {
    if (!enableZipcodeSearch || !zipCodeInput) return;
    let cancelled = false;
    setIsResolvingZips(true);
    getNearbyZipCodes(zipCodeInput, zipcodeResultsDistance).then((zips) => {
      if (cancelled) return;
      setResolvedZipCodes(Array.isArray(zips) && zips.length ? zips : null);
      setIsResolvingZips(false);
    });
    return () => { cancelled = true; };
  }, [zipCodeInput, enableZipcodeSearch, zipcodeResultsDistance]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 5 && /^\d*$/.test(value)) {
      setInputValue(value);
      if (validateZipCode(value)) {
        setZipCodeInput(value);
      } else {
        setZipCodeInput(null);
        setResolvedZipCodes(null);
      }
    }
  };

  const baseFilter = useMemo(() => {
    const f: Record<string, any> = {};
    if (speciality) {
      const list = speciality.split(",").map((s) => s.trim()).filter(Boolean);
      if (list.length === 1) f.specialty = list[0];
      else if (list.length > 1) f.specialty = list;
    }
    if (resolvedZipCodes && resolvedZipCodes.length) {
      f.zipcode = resolvedZipCodes;
    }
    return f;
  }, [speciality, resolvedZipCodes]);

  const showSelect = !enableZipcodeSearch || resolvedZipCodes !== null;

  const DoctorsLoading = (
    <div style={{ width: "100%", textAlign: "center", fontWeight: "bold", fontStyle: "italic", color: "#666" }}>
      <span dangerouslySetInnerHTML={{ __html: doctorsLoadingText }} />
    </div>
  );

  return (
    <>
      {zipSearchVisible ? (
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={zipcodePlaceholder}
          style={{
            border: "1px solid #d4d4d4",
            borderRadius: "6px",
            marginBottom: "20px",
            boxSizing: "border-box",
            color: "rgba(0, 0, 0, 0.8)",
            padding: "18px 20px",
            width: "100%",
            fontSize: "16px",
            backgroundColor: "rgb(255, 255, 255)",
            borderColor: "rgb(191, 191, 191)",
            borderWidth: "1px",
            boxShadow: "rgba(0, 0, 0, 0.25) 0px 2px 4px 0px inset",
          }}
        />
      ) : null}

      {showSelect ? (
        <VirtualizedAsyncSelect
          fieldData={fieldData}
          baseFilter={baseFilter}
          isManualInput={isManualInput}
          onStateChangeManualInput={handleChildStateChange}
          errors={errors}
          control={control}
        />
      ) : (
        isResolvingZips ? <Loader /> : DoctorsLoading
      )}

      {manualDoctorInput &&
        <ManualInput
          manualDoctorInput={manualDoctorInput}
          isManualInput={isManualInput}
          onStateChangeManualInput={handleChildStateChange}
          register={register}
          errors={errors}
        />}
    </>
  );
};

export default DoctorsDropdown;
